import { useState } from "react";
import {
  CheckIcon,
  XMarkIcon,
  PencilIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

interface EditableFieldProps {
  fieldName: string;
  value: string | string[];
  isEditing: boolean;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onChange: (value: string) => void;
  onArrayChange?: (value: string[]) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: "text" | "array";
  newItemValue?: string;
  onNewItemChange?: (value: string) => void;
  onAddItem?: () => void;
  onRemoveItem?: (index: number) => void;
}

export default function EditableField({
  fieldName,
  value,
  isEditing,
  onStartEdit,
  onSave,
  onCancel,
  onChange,
  onArrayChange,
  onKeyDown,
  placeholder,
  type = "text",
  newItemValue = "",
  onNewItemChange,
  onAddItem,
  onRemoveItem,
}: EditableFieldProps) {
  if (type === "array" && Array.isArray(value)) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-light tracking-wider uppercase text-gray-500 dark:text-gray-400">
            {fieldName}
          </span>
          {!isEditing && (
            <button
              onClick={onStartEdit}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {value.map((item, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                >
                  {item}
                  <button
                    onClick={() => onRemoveItem?.(index)}
                    className="ml-2 text-gray-500 hover:text-red-500 transition-colors"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            
            <div className="flex space-x-2">
              <input
                type="text"
                value={newItemValue}
                onChange={(e) => onNewItemChange?.(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                className="flex-1 px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
              />
              <button
                onClick={onAddItem}
                disabled={!newItemValue.trim()}
                className="p-1 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={onSave}
                className="p-1 bg-green-600 text-white rounded-lg hover:scale-105 transition-transform"
              >
                <CheckIcon className="h-4 w-4" />
              </button>
              <button
                onClick={onCancel}
                className="p-1 bg-red-600 text-white rounded-lg hover:scale-105 transition-transform"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {value.length > 0 ? (
              value.map((item, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                >
                  {item}
                </span>
              ))
            ) : (
              <span className="text-gray-500 dark:text-gray-400 text-sm italic">
                No {fieldName.toLowerCase()} added yet
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-light tracking-wider uppercase text-gray-500 dark:text-gray-400">
          {fieldName}
        </span>
        {!isEditing && (
          <button
            onClick={onStartEdit}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="flex space-x-2">
          <input
            type="text"
            value={value as string}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
          />
          <button
            onClick={onSave}
            className="p-2 bg-green-600 text-white rounded-lg hover:scale-105 transition-transform"
          >
            <CheckIcon className="h-4 w-4" />
          </button>
          <button
            onClick={onCancel}
            className="p-2 bg-red-600 text-white rounded-lg hover:scale-105 transition-transform"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <p className="text-gray-900 dark:text-white font-light">
          {value || (
            <span className="text-gray-500 dark:text-gray-400 italic">
              No {fieldName.toLowerCase()} specified
            </span>
          )}
        </p>
      )}
    </div>
  );
}
